import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { IconComponent } from "@claas/claas-ui-components";
import {
  ToggleComponent,
  ToggleSize,
} from "@claas/claas-ui-components/form/toggle";
import { TranslatePipe } from "@ngx-translate/core";
import { ProductDocumentsTableData } from "./product-documents-view-model";
import { AppSectionHeader } from "../../shared/components/app-section-header/app-section-header";
import { DataDocument } from "../../shared/components/data-document/data-document";
import { EmptyFavoritesList } from "../../shared/components/empty-favorites-list/empty-favorites-list";
import {
  pathRouteDataHelper$,
  pathRouteParametersHelper$,
} from "../../shared/helpers/path-params.helper";
import { DefaultDocCat, DefaultDocRef } from "../../shared/pia-api/generated";
import { parseApiDateHelper } from "../../shared/pia-api/helpers/parse-api-date-helper";
import { FavoriteDocumentsService } from "../../shared/services/favorite-documents.service";

@Component({
  selector: "claas-product-documents",
  imports: [
    DataDocument,
    IconComponent,
    ToggleComponent,
    TranslatePipe,
    AppSectionHeader,
    EmptyFavoritesList,
  ],
  templateUrl: "./product-documents.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./product-documents.scss",
})
export class ProductDocumentsComponent {
  private route = inject(ActivatedRoute);
  private favoriteDocumentsService = inject(FavoriteDocumentsService);
  protected readonly toggleSize = ToggleSize;

  showFavoriteDocsStatus = toSignal(
    this.favoriteDocumentsService.productDocumentFavorites$,
  );
  favoriteProductDocuments = toSignal(
    this.favoriteDocumentsService.includedDocumentIds$,
  );
  data = toSignal(pathRouteDataHelper$());
  routeParams = toSignal(pathRouteParametersHelper$());
  isChecked = computed(() => {
    return this.showFavoriteDocsStatus() as boolean;
  });
  productCategoryTitle = computed(() => {
    const productCategories = this.data()?.[
      "productDocumentCategories"
    ] as DefaultDocCat[];
    const selectedCategory = productCategories.find(
      (productCategory) =>
        productCategory.ID === this.routeParams()?.["docCatId"],
    );
    return selectedCategory?.Title ?? null;
  });
  documentTypes = computed<ProductDocumentsTableData[]>(() => {
    const docRefsWithDocumentAndDocumentCategories = this.data()?.[
      "documents"
    ] as DefaultDocRef[];
    const viewDocumentTypes =
      docRefsWithDocumentAndDocumentCategories?.reduce(
        (tableData, docRef: DefaultDocRef) => {
          const tupleIndex = docRef?.Country === "INT" ? 1 : 0;
          // international document
          if (!tableData[tupleIndex]) {
            const title =
              docRef?.Country === "INT"
                ? "internationalDocuments"
                : "localDocuments";
            tableData[tupleIndex] = {
              title,
              tableData: [],
            };
          }

          if (
            docRef.Document?.ID &&
            docRef.Document?.Title &&
            docRef.Document?.Language &&
            docRef.Document.Size &&
            docRef.Document.Type
          ) {
            tableData[tupleIndex].tableData = [
              ...tableData[tupleIndex].tableData,
              {
                id: String(docRef.Document.ID),
                name: docRef.Document.Title,
                language: docRef.Document.Language,
                format: docRef.Document.Type,
                size: String(docRef.Document.Size),
                new: (() => {
                  const modifiedDate = parseApiDateHelper(
                    docRef.Document.Modified,
                  );
                  const now = Date.now();
                  const oneMonthMs = 30 * 24 * 60 * 60 * 1000;
                  return now - modifiedDate <= oneMonthMs;
                })(),
              },
            ];
          }
          return tableData;
        },
        [] as ProductDocumentsTableData[],
      ) ?? [];
    return viewDocumentTypes.filter(
      (item) => item?.title && item?.tableData?.length,
    );
  });
  showFiltered = computed(() => {
    if (this.isChecked()) {
      return this.documentTypes().map((product) => {
        return {
          title: product.title,
          tableData: product.tableData.filter((item) => {
            return this.favoriteProductDocuments()?.some(
              (id) => id === item?.id,
            );
          }),
        };
      });
    }
    return this.documentTypes();
  });

  checkIfDocumentsIncluded = computed(() => {
    return this.documentTypes().some((document) => {
      return document.tableData.some((data) => {
        return this.favoriteProductDocuments()?.includes(data.id);
      });
    });
  });

  hasAnyNewDocuments = computed(() => {
    const filteredDocuments = this.showFiltered();

    return filteredDocuments.some((product) => {
      return product.tableData.some((document) => document.new === true);
    });
  });

  handleToggleFavouriteDocsChange(checked: boolean) {
    this.favoriteDocumentsService.setFavoritesDocumentsStatus(checked);
  }
}
